
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import KnowledgeBase from "@/components/feedback/knowledge/KnowledgeBase";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import NotFound from "@/pages/NotFound";
import CompanyMetadata from "@/components/shared/CompanyMetadata";
import type { Company } from "@/types";

const HelpDesk = ({ customSlug = "" }: { customSlug?: string }) => {
  const { slug: urlSlug } = useParams();
  const slug = customSlug || urlSlug;
  
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Ensure HelpDesk is always in light mode by removing any theme classes
    document.documentElement.classList.remove('dark', 'admin-dark');
    
    const fetchCompany = async () => {
      try {
        if (!slug) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("companies")
          .select("*")
          .eq("slug", slug)
          .single();

        if (error) {
          console.error("Error fetching company:", error);
          setIsLoading(false);
          return;
        }

        setCompany(data);
      } catch (error) {
        console.error("Unexpected error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [slug]);

  const navigateToFeedbackPortal = () => {
    // Check if we're on a custom domain
    const hostname = window.location.hostname;
    
    if (hostname === 'help.feednatic.com') {
      // Special case for feednatic.com
      window.location.href = 'https://feedback.feednatic.com';
      return;
    } else if (hostname.startsWith('help.')) {
      // If on a custom domain, redirect to the feedback subdomain
      const domain = hostname.substring(5); // Remove 'help.'
      window.location.href = `https://feedback.${domain}`;
      return;
    }
    
    // Otherwise use the standard slug routing
    window.location.href = `/${company?.slug}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!company) {
    return <NotFound />;
  }

  // Only render CompanyMetadata when we have valid company data with a name
  const hasValidCompanyName = company && 
    typeof company === 'object' && 
    typeof company.name === 'string' &&
    company.name.trim().length > 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {hasValidCompanyName && <CompanyMetadata company={company} pageType="help" />}
      
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {company.logo_url ? (
                <img
                  src={company.logo_url}
                  alt={`${company.name} logo`}
                  className="h-10 w-10 object-contain"
                />
              ) : (
                <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold">{company.name}</h1>
                <p className="text-sm text-muted-foreground">Knowledge Base</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={navigateToFeedbackPortal}
              >
                Feedback Portal
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="max-w-4xl mx-auto">
          <KnowledgeBase companyId={company.id} />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} {company.name}. All rights reserved.
            </p>
            <p className="text-sm text-muted-foreground">
              <a
                href="https://feednatic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                Powered by Feednatic
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpDesk;
